import { DeleteDialog } from '@retail/components';
import { useToast } from '@retail/hooks';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { AssortmentTag } from '@retail/my-assortment/types';
import { useDeleteSupplierItemAssortmentTag } from '@retail/products/data-access';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

interface Props {
  deleteItemAssortmentTag: AssortmentTag;
  supplierItemId: number;
  assortmentId: number;
  onClose: () => void;
}

export function DeleteItemFromAssortmentTag({
  deleteItemAssortmentTag,
  supplierItemId,
  assortmentId,
  onClose,
}: Props) {
  const t = useMyAssortmentTFunction();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteSupplierItemFromAssortmentTag } = useDeleteSupplierItemAssortmentTag();

  const handleDelete = useCallback(
    () =>
      deleteSupplierItemFromAssortmentTag({
        urlVariables: { tagId: deleteItemAssortmentTag.id, assortmentId, supplierItemId },
      })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['mg-item'] });
          queryClient.invalidateQueries({ queryKey: ['mg-item-assortment-tags'] });
          queryClient.invalidateQueries({ queryKey: ['assortmentItems'] });
          queryClient.invalidateQueries({ queryKey: ['central-assortments'] });
          toast.success(
            t('myAssortment.actions.deletedItem', {
              assortmentName: deleteItemAssortmentTag?.name,
            })
          );
        })
        .catch((err) => {
          console.error(err);
          toast.error(t('myAssortment.actions.genericActionError'));
        }),
    [
      assortmentId,
      deleteItemAssortmentTag.id,
      deleteItemAssortmentTag?.name,
      deleteSupplierItemFromAssortmentTag,
      queryClient,
      supplierItemId,
      t,
      toast,
    ]
  );

  return (
    <DeleteDialog
      isOpen
      dialogTitle={t('myAssortment.actions.deleteItem')}
      message={t('myAssortment.actions.deleteItemFromAssortmentMessage', {
        assortmentName: deleteItemAssortmentTag.name,
      })}
      onDelete={handleDelete}
      onClose={onClose}
    />
  );
}
